import React from 'react';
import { Sparkles, Heart, Palette, Shield } from 'lucide-react';

const highlights = [
  {
    icon: Palette,
    title: 'Aesthetic Obsessed',
    description: 'We turn boring, factory-standard spaces into stunning visual experiences. Every poster and sticker is designed to make your setup look and feel premium.',
  },
  {
    icon: Sparkles,
    title: 'Built to Last',
    description: 'Premium materials, vibrant colors that don\'t fade, and a finish that feels expensive — but at prices everyone can afford. Quality you can touch.',
  },
  {
    icon: Heart,
    title: 'You First, Always',
    description: 'Customer satisfaction isn\'t just a slogan — it\'s our obsession. From order to delivery, we make sure you love every single piece.',
  },
  {
    icon: Shield,
    title: 'Your Dream Setup',
    description: 'Want a customised collection? We build dream setups with personalised stickers and posters of your choice. Your vision, our craft.',
  },
];

const AboutUs: React.FC = () => {
  return (
    <section id="about" className="py-24 bg-neutral-950 relative overflow-hidden">
      {/* Background decorations */}
      <div className="absolute top-0 left-0 w-[500px] h-[500px] bg-amber-900/8 rounded-full blur-[120px] pointer-events-none -translate-x-1/3 -translate-y-1/3"></div>
      <div className="absolute bottom-0 right-0 w-[400px] h-[400px] bg-pink-900/8 rounded-full blur-[100px] pointer-events-none translate-x-1/3 translate-y-1/3"></div>

      <div className="container mx-auto px-6 relative z-10">
        {/* Header */}
        <div className="max-w-3xl mx-auto text-center mb-16">
          <h2 className="text-xs font-bold tracking-[0.4em] text-amber-500 mb-4 uppercase">Who We Are</h2>
          <p className="text-3xl md:text-5xl font-black tracking-tighter uppercase mb-6 leading-tight">
            We Make Boring 
            <span className="text-amber-500"> Beautiful</span>
          </p>
          <p className="text-neutral-400 text-sm md:text-base leading-relaxed max-w-2xl mx-auto">
            YUMECAV was born from one idea — your space should reflect who you are, not what came out of a factory. 
            We create affordable, long-lasting, premium-feel posters and stickers that transform any room, 
            desk, or device into something you're proud to show off.
          </p>
        </div>

        {/* Highlights Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto mb-16">
          {highlights.map((item, index) => (
            <div
              key={index}
              className="group relative p-6 md:p-8 rounded-2xl bg-neutral-900/60 border border-white/5 hover:border-amber-500/20 transition-all duration-500 hover:bg-neutral-900"
            >
              {/* Hover glow */}
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-amber-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"></div>

              <div className="relative z-10">
                <div className="p-3 bg-amber-500/10 rounded-xl w-fit mb-4 group-hover:bg-amber-500/20 transition-colors duration-300">
                  <item.icon className="w-5 h-5 text-amber-400" strokeWidth={2} />
                </div>
                <h3 className="text-white font-bold text-base md:text-lg uppercase tracking-wide mb-2">
                  {item.title}
                </h3>
                <p className="text-neutral-400 text-sm leading-relaxed">
                  {item.description}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA / Tagline */}
        <div className="text-center">
          <div className="inline-flex items-center gap-3 px-6 py-3 bg-neutral-900 border border-white/10 rounded-full">
            <span className="w-2 h-2 bg-amber-500 rounded-full animate-pulse"></span>
            <span className="text-[11px] font-black uppercase tracking-[0.2em] text-neutral-300">
              Default is Death — Make It Yours
            </span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutUs;
